import React, { useMemo, useState } from 'react';
import { Search, RefreshCw, X } from 'lucide-react';
import { SortableTableHead, NumberedPagination } from '@kioskgaming/ui';
import { GameWallet, GameWalletFilters, GameWalletSyncStatus } from '../../../../types';
import { useGameWallets } from '../../../../hooks/useApi';
import { useFormatDate } from '../../../../hooks/useFormatDate';
import { cn, formatCurrency } from '../../../../utils';
import { getTableSortColumns } from '../../../../config/tableSortConfig';
import { resolveHeaderSort, useTableSort } from '../../../../hooks/useTableSort';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../../ui/Table';
import Input from '../../../ui/Input';
import Button from '../../../ui/Button';
import { LoadingSpinner } from '../../../ui/Loading';
import GameWalletDrawer from '../GameWalletDrawer';

interface WalletsTabProps {
  providerId: string;
  providerName?: string;
}

const syncStatusConfig: Record<GameWalletSyncStatus, { label: string; className: string }> = {
  never_synced: { label: 'Never Synced', className: 'bg-gray-100 text-gray-800' },
  in_sync: { label: 'In Sync', className: 'bg-green-100 text-green-800' },
  pending: { label: 'Pending', className: 'bg-yellow-100 text-yellow-800' },
  failed: { label: 'Failed', className: 'bg-red-100 text-red-800' },
};

const WalletsTab: React.FC<WalletsTabProps> = ({ providerId, providerName }) => {
  const fmt = useFormatDate();
  const [filters, setFilters] = useState<GameWalletFilters>({
    page: 1,
    limit: 50,
    gameProviderId: providerId,
  });
  const [selectedWallet, setSelectedWallet] = useState<GameWallet | null>(null);

  const tableSort = useTableSort({
    columns: getTableSortColumns('gameWallets'),
    paramPrefix: 'gw',
    onChange: () => setFilters((prev) => ({ ...prev, page: 1 })),
  });

  const queryFilters = useMemo((): GameWalletFilters => {
    const next: GameWalletFilters = { ...filters };
    const { sortBy, sortOrder } = tableSort.sortParams;
    if (sortBy === 'walletBalance' || sortBy === 'inGameBalance' || sortBy === 'lastSyncedAt') {
      next.sortBy = sortBy;
      next.sortOrder = sortOrder;
    }
    return next;
  }, [filters, tableSort.sortParams]);

  const { data, isLoading, error, refetch, isFetching } = useGameWallets(queryFilters);

  const appliedSort = useMemo(
    () =>
      data?.data?.sortBy
        ? {
            sortBy: data.data.sortBy ?? null,
            sortOrder: data.data.sortOrder ?? null,
          }
        : null,
    [data],
  );

  const headerSort = resolveHeaderSort(tableSort, appliedSort);
  const isTableBusy = isLoading || isFetching;

  const wallets = data?.data?.items ?? [];
  const pagination = data?.data ?? { total: 0, page: 1, limit: 50 };
  const totalPages = Math.ceil(pagination.total / pagination.limit);

  const handleSearch = (value: string) => {
    setFilters(prev => ({ ...prev, search: value.trim() || undefined, page: 1 }));
  };

  const handleSyncStatusFilter = (value: string) => {
    setFilters(prev => ({
      ...prev,
      syncStatus: value === 'all' ? undefined : value as GameWalletSyncStatus,
      page: 1,
    }));
  };

  const handleClear = () => {
    setFilters({ page: 1, limit: 50, gameProviderId: providerId });
    tableSort.resetSort();
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-48 gap-2">
        <p className="text-destructive text-sm">Failed to load wallets</p>
        <button onClick={() => refetch()} className="text-xs text-primary hover:underline">
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Section header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight">Wallets</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Default tab. Use this page to investigate player game wallet balances and sync status.
          </p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-muted-foreground mb-1">Search</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Player phone, email, or game account…"
              value={filters.search ?? ''}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-9 pr-8"
            />
            {filters.search && (
              <button
                onClick={() => handleSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        <div className="min-w-[160px]">
          <label className="block text-xs font-medium text-muted-foreground mb-1">Sync Status</label>
          <select
            value={filters.syncStatus ?? 'all'}
            onChange={(e) => handleSyncStatusFilter(e.target.value)}
            className="w-full h-10 px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="all">All Status</option>
            <option value="in_sync">In Sync</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
            <option value="never_synced">Never Synced</option>
          </select>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleClear}>
            <X className="h-4 w-4 mr-1" />
            Clear
          </Button>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isTableBusy}>
            <RefreshCw className={cn('h-4 w-4 mr-1', isTableBusy && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : (
        <>
          <div className="rounded-md border overflow-x-auto">
            <Table aria-busy={isTableBusy || undefined}>
              <TableHeader>
                <TableRow>
                  <TableHead>Player</TableHead>
                  <TableHead>Game Wallet ID</TableHead>
                  <SortableTableHead
                    sortable
                    label="Temp Wallet Balance"
                    sortKey="walletBalance"
                    activeKey={headerSort.activeKey}
                    direction={headerSort.direction}
                    onSort={tableSort.toggleSort}
                    disabled={isTableBusy}
                    align="right"
                  />
                  <SortableTableHead
                    sortable
                    label="In-Game Balance"
                    sortKey="inGameBalance"
                    activeKey={headerSort.activeKey}
                    direction={headerSort.direction}
                    onSort={tableSort.toggleSort}
                    disabled={isTableBusy}
                    align="right"
                  />
                  <TableHead>Sync Status</TableHead>
                  <SortableTableHead
                    sortable
                    label="Last Sync"
                    sortKey="lastSyncedAt"
                    activeKey={headerSort.activeKey}
                    direction={headerSort.direction}
                    onSort={tableSort.toggleSort}
                    disabled={isTableBusy}
                  />
                  <TableHead className="text-center">Open</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className={cn(isTableBusy && 'opacity-60 transition-opacity')}>
                {wallets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-12">
                      No wallets found
                    </TableCell>
                  </TableRow>
                ) : (
                  wallets.map((wallet) => {
                    const statusCfg = syncStatusConfig[wallet.syncStatus] ?? { label: wallet.syncStatus, className: 'bg-gray-100 text-gray-800' };
                    return (
                      <TableRow
                        key={wallet.id}
                        className="cursor-pointer hover:bg-muted/40"
                        onClick={() => setSelectedWallet(wallet)}
                      >
                        <TableCell>
                          <div className="text-sm font-medium">
                            {wallet.user?.email || wallet.user?.phone || '—'}
                          </div>
                          {wallet.gameUsername && (
                            <div className="text-xs text-muted-foreground">{wallet.gameUsername}</div>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="font-mono text-xs text-muted-foreground">{wallet.id.slice(0, 12)}…</span>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatCurrency(parseFloat(wallet.walletBalance))}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatCurrency(parseFloat(wallet.inGameBalance))}
                        </TableCell>
                        <TableCell>
                          <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', statusCfg.className)}>
                            {statusCfg.label}
                          </span>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {wallet.lastSyncedAt ? fmt(wallet.lastSyncedAt) : '—'}
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                            View
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="flex justify-between items-center text-sm text-muted-foreground">
              <span>Showing {wallets.length} of {pagination.total} wallets</span>
              <NumberedPagination
                currentPage={pagination.page}
                totalPages={totalPages}
                onPageChange={(page) => setFilters(prev => ({ ...prev, page }))}
              />
            </div>
          )}
          {totalPages <= 1 && (
            <p className="text-xs text-muted-foreground">
              {pagination.total} wallet{pagination.total !== 1 ? 's' : ''} for {providerName ?? 'this provider'}
              {tableSort.sortBy && (
                <>
                  {' · '}
                  Sorted by {tableSort.getColumn(tableSort.sortBy)?.label ?? tableSort.sortBy}{' '}
                  ({tableSort.sortOrder === 'ASC' ? 'ascending' : 'descending'})
                </>
              )}
            </p>
          )}
        </>
      )}

      <GameWalletDrawer
        wallet={selectedWallet}
        isOpen={selectedWallet !== null}
        onClose={() => setSelectedWallet(null)}
        onRefresh={() => refetch()}
      />
    </div>
  );
};

export default WalletsTab;
